import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Map from '../components/Google-Maps/Map';
import '../assets/styles/Admision.css';

const ADMISSION_URL = 'https://admision.mineduc.cl/registro/';

const faqs = [
  {
    question: '¿Cómo puedo postular al colegio?',
    answer:
      'Las postulaciones se realizan a través del Sistema de Admisión Escolar (SAE) del MINEDUC. Debes registrarte como apoderado/a en admision.mineduc.cl, buscar el establecimiento "Le Beau Jour" y completar la postulación en línea.',
  },
  {
    question: '¿En qué periodo del año se realizan las postulaciones?',
    answer:
      'El proceso de admisión del MINEDUC se realiza una vez al año, generalmente durante el segundo semestre. Te recomendamos revisar las fechas actualizadas en admision.mineduc.cl o contactarnos directamente para más información.',
  },
  {
    question: '¿Qué niveles educativos ofrece el colegio?',
    answer:
      'Ofrecemos educación parvularia y enseñanza básica (1° a 8° básico), con una enseñanza personalizada y una sólida formación en valores cristianos.',
  },
  {
    question: '¿El colegio es particular subvencionado?',
    answer:
      'Sí, somos un establecimiento educacional particular subvencionado, con reconocimiento oficial del Ministerio de Educación de Chile.',
  },
  {
    question: '¿Dónde está ubicado el colegio?',
    answer:
      'Estamos ubicados en 23 Norte Calle Dos 2039, Viña del Mar. Puedes ver la ubicación exacta en el mapa de esta página o abrir la ruta directamente en Google Maps.',
  },
  {
    question: '¿Cómo puedo resolver dudas sobre el proceso de admisión?',
    answer:
      'Puedes llamarnos al (+56) 32 3241150, escribirnos a colegio_lebeaujour@hotmail.com o visitarnos en nuestro horario de atención de lunes a viernes. Estaremos encantados de acompañarte en el proceso.',
  },
];

function Admision() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="admision-page">
      <Helmet>
        <title>Admisión y Matrículas | Colegio Cristiano Le Beau Jour</title>
        <meta
          name="description"
          content="Matrículas abiertas en el Colegio Cristiano Le Beau Jour de Viña del Mar. Conoce cómo postular a través del Sistema de Admisión Escolar del MINEDUC, nuestra propuesta educativa, ubicación y datos de contacto."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      {/* Bienvenida */}
      <section className="admision-hero">
        <div className="admision-hero-content">
          <p className="admision-eyebrow">Colegio Cristiano Le Beau Jour</p>
          <h1>Admisión y Matrículas</h1>
          <div className="admision-welcome">
            <p className="admision-welcome-title">¡Bienvenidos/as a nuestro colegio!</p>
            <p>
              En el Colegio Cristiano Le Beau Jour acompañamos la formación de niños y
              niñas desde una propuesta educativa con más de 40 años de trayectoria en
              Viña del Mar, donde la tradición y la innovación se unen para entregar
              una educación integral, cercana y comprometida con el desarrollo de cada
              estudiante.
            </p>
            <p>
              Somos un colegio particular subvencionado, mixto y cristiano, que ofrece
              educación parvularia y básica con una enseñanza personalizada.
              Inspirados en nuestro lema, integramos la excelencia académica, la
              formación en valores y el desarrollo personal, preparando a nuestros
              estudiantes para descubrir sus talentos y aportar positivamente a la
              sociedad.
            </p>
            <blockquote className="admision-quote">
              “Más de 40 años educando al abrigo del Altísimo, formando generaciones
              que transforman vidas.”
            </blockquote>
          </div>
          <a
            className="admision-cta"
            href={ADMISSION_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            ¡Matricúlate Aquí!
          </a>
        </div>
      </section>

      {/* Cómo postular */}
      <section className="admision-section">
        <h2>¿Cómo postular?</h2>
        <p className="admision-section-intro">
          El proceso de admisión se realiza en línea a través del Sistema de Admisión
          Escolar (SAE) del MINEDUC. Solo debes seguir estos pasos:
        </p>
        <div className="admision-steps-grid">
          <div className="admision-step-card">
            <span className="admision-step-number">1</span>
            <h3>Regístrate como apoderado</h3>
            <p>
              Ingresa a <strong>admision.mineduc.cl</strong> y crea tu cuenta de
              apoderado/a en el Sistema de Admisión Escolar.
            </p>
          </div>
          <div className="admision-step-card">
            <span className="admision-step-number">2</span>
            <h3>Busca el colegio</h3>
            <p>
              En el buscador del portal, encuentra el establecimiento{' '}
              <strong>“Le Beau Jour”</strong> de Viña del Mar.
            </p>
          </div>
          <div className="admision-step-card">
            <span className="admision-step-number">3</span>
            <h3>Completa tu postulación</h3>
            <p>
              Selecciona el nivel, completa la información requerida y envía tu
              postulación en línea.
            </p>
          </div>
        </div>
        <div className="admision-section-cta">
          <a
            className="admision-cta"
            href={ADMISSION_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ir al portal de admisión
          </a>
        </div>
      </section>

      {/* Por qué elegirnos */}
      <section className="admision-section admision-why">
        <h2>¿Por qué elegir el Le Beau Jour?</h2>
        <div className="admision-features-grid">
          <div className="admision-feature-card">
            <i className="fas fa-heart"></i>
            <h3>Formación en valores cristianos</h3>
            <p>
              Educamos al abrigo del Altísimo, formando estudiantes íntegros,
              solidarios y comprometidos con los demás.
            </p>
          </div>
          <div className="admision-feature-card">
            <i className="fas fa-language"></i>
            <h3>Enseñanza bilingüe</h3>
            <p>
              Una enseñanza bilingüe personalizada que potencia el desarrollo
              cognitivo y comunicativo de cada estudiante.
            </p>
          </div>
          <div className="admision-feature-card">
            <i className="fas fa-graduation-cap"></i>
            <h3>Excelencia académica</h3>
            <p>
              Metodologías innovadoras y una cultura de altas expectativas para el
              crecimiento académico y emocional.
            </p>
          </div>
          <div className="admision-feature-card">
            <i className="fas fa-award"></i>
            <h3>Más de 40 años de trayectoria</h3>
            <p>
              Desde 1983 formando generaciones en Viña del Mar con un sello educativo
              cercano y de calidad.
            </p>
          </div>
        </div>
      </section>

      {/* Preguntas frecuentes */}
      <section className="admision-section">
        <h2>Preguntas Frecuentes</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div
              className={`faq-item ${openFaqIndex === index ? 'open' : ''}`}
              key={faq.question}
            >
              <button
                type="button"
                className="faq-question"
                aria-expanded={openFaqIndex === index}
                onClick={() => toggleFaq(index)}
              >
                <span>{faq.question}</span>
                <i className="fas fa-chevron-down faq-icon"></i>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Ubicación */}
      <section className="admision-section admision-location">
        <h2>¿Dónde estamos?</h2>
        <p className="admision-section-intro">
          <i className="fas fa-map-marker-alt"></i> 23 Norte Calle Dos 2039, Viña del
          Mar
        </p>
        <div className="admision-map">
          <Map />
        </div>
        <div className="admision-section-cta">
          <a
            className="admision-secondary-link"
            href="https://maps.app.goo.gl/9VzbA1atufTZ1WJQ6"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver ruta en Google Maps
          </a>
        </div>
      </section>

      {/* Contacto */}
      <section className="admision-section admision-contact">
        <h2>¿Tienes dudas? Contáctanos</h2>
        <div className="admision-contact-grid">
          <div className="admision-contact-item">
            <i className="fas fa-phone-square-alt"></i>
            <p className="admision-contact-label">Teléfono</p>
            <a href="tel:+56323241150">(+56) 32 3241150</a>
          </div>
          <div className="admision-contact-item">
            <i className="fas fa-envelope"></i>
            <p className="admision-contact-label">Correo electrónico</p>
            <a href="mailto:colegio_lebeaujour@hotmail.com">
              colegio_lebeaujour@hotmail.com
            </a>
          </div>
          <div className="admision-contact-item">
            <i className="fas fa-clock"></i>
            <p className="admision-contact-label">Horario de atención</p>
            <p>Lunes a Viernes · 08:00 a 17:00 hrs</p>
          </div>
        </div>
        <div className="admision-social">
          <p>Síguenos en nuestras redes sociales</p>
          <div className="admision-social-icons">
            <a
              href="https://www.facebook.com/CollegeLeBeauJour/?locale=es_LA"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook del colegio"
            >
              <i className="fab fa-facebook"></i>
            </a>
            <a
              href="https://www.instagram.com/colegiolebeaujour/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram del colegio"
            >
              <i className="fab fa-instagram"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="admision-final-cta">
        <h2>¡Te esperamos!</h2>
        <p>
          Da el primer paso para que tu hijo o hija sea parte de la familia Le Beau
          Jour. Postula hoy a través del portal oficial del MINEDUC.
        </p>
        <a
          className="admision-cta"
          href={ADMISSION_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          ¡Matricúlate Aquí!
        </a>
      </section>
    </div>
  );
}

export default Admision;
